import React, { memo, useState } from "react";
import {
  Autocomplete,
  Box, Button, Checkbox, FormControlLabel, Grid, IconButton, MenuItem, TextField, Typography,
} from "@mui/material";
import IpdTextField from "../../../../utils/IpdTextField";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import FormField from "../FormField";
import ConfigurableField from "../ConfigurableField";
import { ADDRESS_TYPES } from "../../constants/registrationConstants";
import { CONFIGURED_SECTION_IDS } from "../../utils/sectionConfiguration";
import { useSectionConfig } from "../../context/SectionConfigContext";

const S = CONFIGURED_SECTION_IDS;

const EMPTY_ADDRESS_FIELDS = {
  line1: "",
  city: "",
  district: "",
  state: "",
  country: "India",
  pincode: "",
};

function Addresses({
  patient, setPatient, updateListItem, addListItem, removeListItem, savedAddresses, errors = {},
  states = [], districtsMap = {}, fetchDistricts,
}) {
  const { canAddListEntry, canRemoveListEntry, isFieldActive, config } = useSectionConfig();
  const [sameAsPrevious, setSameAsPrevious] = useState({});

  const handleSameAsPrevious = (index, checked) => {
    if (checked) {
      const src = patient.addresses[index - 1];
      setPatient((s) => ({
        ...s,
        addresses: s.addresses.map((x, idx) => (idx === index ? { ...src, type: x.type } : x)),
      }));
      setSameAsPrevious((prev) => ({ ...prev, [index]: true }));
    } else {
      setPatient((s) => ({
        ...s,
        addresses: s.addresses.map((x, idx) => (
          idx === index ? { type: x.type, ...EMPTY_ADDRESS_FIELDS } : x
        )),
      }));
      setSameAsPrevious((prev) => ({ ...prev, [index]: false }));
    }
  };

  const handleRemoveAddress = (index) => {
    removeListItem("addresses", index);
    setSameAsPrevious((prev) => {
      const next = { ...prev };
      delete next[index];
      return next;
    });
  };

  const showType = !config || isFieldActive(S.addresses, "type");

  // State name options for the dropdown (just the name strings)
  const stateOptions = states.map((s) => s.state_name || s).filter(Boolean);

  return (
    <Box>
      {errors.addresses__min && (
        <Typography variant="caption" color="error" sx={{ display: "block", mb: 1 }}>
          {errors.addresses__min}
        </Typography>
      )}
      {patient.addresses.map((a, i) => {
        const districtOptions = (districtsMap[a.state] ?? []).map(
          (d) => (typeof d === "string" ? d : d.district_name ?? d.name ?? ""),
        ).filter(Boolean);

        return (
          <Box key={i} sx={{ border: 1, borderColor: "divider", borderRadius: 1, p: 2, mb: 2 }}>
            <Box display="flex" flexWrap="wrap" alignItems="center" justifyContent="space-between" gap={1} mb={2}>
              {showType && (
                <IpdTextField select size="small" sx={{ minWidth: 160 }} value={a.type} onChange={(e) => updateListItem("addresses", i, "type", e.target.value)}>
                  {ADDRESS_TYPES.map((x) => <MenuItem key={x} value={x}>{x}</MenuItem>)}
                </IpdTextField>
              )}
              <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                {i > 0 && (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={Boolean(sameAsPrevious[i])}
                        onChange={(e) => handleSameAsPrevious(i, e.target.checked)}
                      />
                    }
                    label={<Box component="span" sx={{ fontSize: "0.75rem" }}>Same as previous</Box>}
                  />
                )}
                {savedAddresses.length > 0 && (
                  <IpdTextField
                    select
                    size="small"
                    sx={{ minWidth: 180 }}
                    value=""
                    onChange={(e) => {
                      const src = savedAddresses[parseInt(e.target.value, 10)];
                      if (!src) return;
                      setPatient((s) => ({
                        ...s,
                        addresses: s.addresses.map((x, idx) => (idx === i ? { ...src, type: x.type } : x)),
                      }));
                    }}
                    SelectProps={{ displayEmpty: true }}
                  >
                    <MenuItem value="" disabled>Copy from saved…</MenuItem>
                    {savedAddresses.map((sa, idx) => (
                      <MenuItem key={idx} value={String(idx)}>{sa.line1 || "(empty)"} · {sa.city}</MenuItem>
                    ))}
                  </IpdTextField>
                )}
                {canRemoveListEntry(S.addresses, patient.addresses.length) && (
                  <IconButton aria-label="Remove address" onClick={() => handleRemoveAddress(i)}><DeleteIcon /></IconButton>
                )}
              </Box>
            </Box>
            <Grid container spacing={2}>
              <ConfigurableField sectionId={S.addresses} fieldName="line1" xs={12}>
                {({ required }) => (
                  <FormField label="Address Line" required={required} error={errors[`addresses.${i}.line1`]}>
                    <IpdTextField fullWidth size="small" value={a.line1} onChange={(e) => updateListItem("addresses", i, "line1", e.target.value)} error={Boolean(errors[`addresses.${i}.line1`])} />
                  </FormField>
                )}
              </ConfigurableField>
              <ConfigurableField sectionId={S.addresses} fieldName="country" xs={12} md={4}>
                {({ required }) => (
                  <FormField label="Country" required={required} error={errors[`addresses.${i}.country`]}>
                    <IpdTextField fullWidth size="small" value={a.country} onChange={(e) => updateListItem("addresses", i, "country", e.target.value)} error={Boolean(errors[`addresses.${i}.country`])} />
                  </FormField>
                )}
              </ConfigurableField>
              <ConfigurableField sectionId={S.addresses} fieldName="state" xs={12} md={4}>
                {({ required }) => (
                  <FormField label="State" required={required} error={errors[`addresses.${i}.state`]}>
                    <Autocomplete
                      freeSolo
                      options={stateOptions}
                      value={a.state}
                      onInputChange={(_e, value) => {
                        updateListItem("addresses", i, "state", value);
                        // pre-fetch districts when a valid state is typed/selected
                        if (fetchDistricts) fetchDistricts(value);
                      }}
                      onChange={(_e, value) => {
                        const v = value ?? "";
                        updateListItem("addresses", i, "state", v);
                        if (fetchDistricts && v) fetchDistricts(v);
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          size="small"
                          error={Boolean(errors[`addresses.${i}.state`])}
                          placeholder="Select or type state"
                        />
                      )}
                    />
                  </FormField>
                )}
              </ConfigurableField>
              <ConfigurableField sectionId={S.addresses} fieldName="district" xs={12} md={4}>
                {({ required }) => (
                  <FormField label="District" required={required} error={errors[`addresses.${i}.district`]}>
                    <Autocomplete
                      freeSolo
                      options={districtOptions}
                      value={a.district}
                      onInputChange={(_e, value) => updateListItem("addresses", i, "district", value)}
                      onChange={(_e, value) => updateListItem("addresses", i, "district", value ?? "")}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          size="small"
                          error={Boolean(errors[`addresses.${i}.district`])}
                          placeholder="Select or type district"
                        />
                      )}
                    />
                  </FormField>
                )}
              </ConfigurableField>
              <ConfigurableField sectionId={S.addresses} fieldName="city" xs={12} md={6}>
                {({ required }) => (
                  <FormField label="City / Village" required={required} error={errors[`addresses.${i}.city`]}>
                    <IpdTextField fullWidth size="small" value={a.city} onChange={(e) => updateListItem("addresses", i, "city", e.target.value)} error={Boolean(errors[`addresses.${i}.city`])} />
                  </FormField>
                )}
              </ConfigurableField>
              <ConfigurableField sectionId={S.addresses} fieldName="pincode" xs={12} md={6}>
                {({ required }) => (
                  <FormField label="Pincode" required={required} error={errors[`addresses.${i}.pincode`]}>
                    <IpdTextField fullWidth size="small" value={a.pincode} onChange={(e) => updateListItem("addresses", i, "pincode", e.target.value)} error={Boolean(errors[`addresses.${i}.pincode`])} />
                  </FormField>
                )}
              </ConfigurableField>
            </Grid>
          </Box>
        );
      })}
      {canAddListEntry(S.addresses, patient.addresses.length) && (
        <Button
          size="small"
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={() => addListItem("addresses", { type: "CURRENT", ...EMPTY_ADDRESS_FIELDS })}
        >
          Add Address
        </Button>
      )}
    </Box>
  );
}

export default memo(Addresses);
